import { ImageList } from '@mui/material';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ProductGalleryProps } from '../../@types/Components';
import FilesAPI2DModel from '../../models/standard/FilesAPI2DModel';
import FilesAPIProductModel from '../../models/standard/FilesAPIProductModel';
import { useRoomComponentsData } from '../../provider/RoomComponentsDataProvider';
import { PRODUCT_GALLERY_DEFAULT_COLS, PRODUCT_GALLERY_DEFAULT_GAP } from '../../utils/constants';
import { getImageListStyles } from '../../utils/imgUtils';
import Model2DImageListItem from './2DModelImageListItem';
import ProductImageListItem from './ProductImageListItem';
import RoomComponentsSearchPanel from './RoomComponentsSearchPanel';

/**
 * Component that can display the products which will get fetched from our imsFilesApi endpoints.
 * Uses the ImageList from Mui with lazy loading of the resources and caching of fetched products in a provider.
 *
 * @param {ProductGalleryProps} props props object containing the amount of columns to display and possible styles.
 * @returns {JSX.Element}
 */
const RoomComponentsGallery = (props: ProductGalleryProps): JSX.Element => {
  const {
    products,
    models2D,
    attributes,
    categories,
    manufacturers,
    cols = PRODUCT_GALLERY_DEFAULT_COLS,
    styles = {},
    selectedTab,
  } = props;

  const imgListStyles = getImageListStyles();
  const [searchResults, setSearchResults] = useState<(FilesAPIProductModel | FilesAPI2DModel)[]>([]);
  const { isUserSearching } = useRoomComponentsData();
  const intl = useIntl();

  // Use the correct list based on the tab selection
  const displayedComponents = selectedTab === 0 ? products : models2D;

  /**
   * Function to propagate the search results from the product search panel.
   *
   * @param {FilesAPIProductModel[]} searchResults the results of the currently active search.
   */
  const returnResults = (searchResults: (FilesAPIProductModel | FilesAPI2DModel)[]): void => {
    setSearchResults(searchResults);
  };

  return (
    <>
      <RoomComponentsSearchPanel
        key={intl.locale}
        attributes={attributes}
        categories={categories}
        products={products}
        manufacturers={manufacturers}
        returnResults={returnResults}
        showManufacturersFilter={selectedTab === 0}
      />
      <ImageList
        gap={PRODUCT_GALLERY_DEFAULT_GAP}
        cols={cols}
        rowHeight={100}
        sx={{
          ...imgListStyles,
          ...styles,
          display: 'grid !important', // Ensure it's grid-based
          gridTemplateRows: 'repeat(auto-fill, 100px) !important', // Prevent implicit row expansion
          gridAutoRows: '100px !important', // Enforce fixed row heights
          alignItems: 'start', // Align items to the top
          '& .MuiImageListItem-root': {
            height: '100px !important', // Force strict height for each item
          },
        }}
        variant="standard"
      >
        {isUserSearching
          ? searchResults.map((item, index) =>
              selectedTab === 0 ? (
                <ProductImageListItem key={`productImageListItem-${index}`} product={item as FilesAPIProductModel} index={index} />
              ) : (
                <Model2DImageListItem key={`model2DImageListItem-${index}`} model2D={item as FilesAPI2DModel} index={index} />
              ),
            )
          : displayedComponents.map((item, index) =>
              selectedTab === 0 ? (
                <ProductImageListItem key={`productImageListItem-${index}`} product={item as FilesAPIProductModel} index={index} />
              ) : (
                <Model2DImageListItem key={`model2DImageListItem-${index}`} model2D={item as FilesAPI2DModel} index={index} />
              ),
            )}
      </ImageList>
    </>
  );
};

export default RoomComponentsGallery;
